import * as React from 'react';
import {graphql} from 'react-relay';

/**
 * Detail query for the id-based route. The query boundary now lives in the
 * route via the Forecast seam's `ForecastQueryRenderer` (ACC-460); this module
 * owns the operation document and a small fetch-key helper, mirroring
 * `useTicketListQuery.ts`.
 *
 * `viewer { actualPersonId }` is required by `ForecastQueryRenderer`'s
 * logged-in guard.
 */
export const TicketDetailQuery = graphql`
	query useTicketDetailQuery($id: ID!) {
		viewer {
			actualPersonId
			ticketDetail(id: $id) {
				ticket {
					id
					slug
					subject
					description
					status
					priority
					channel
					assigneeId
					assignee {
						fullName
						...ticketingPerson_person
					}
					requester {
						id
						name
						email
					}
					createdAt
					updatedAt
					closedAt
					links {
						__typename
						linkType
						targetTicket {
							id
						}
						targetProject {
							id
						}
					}
					timeEntries {
						id
					}
					...TicketDetailHeader_ticket
					...TicketMetaPanel_ticket
					...RelatedTab_ticket
					...LinkedTasksTab_ticket
					...TimeTab_ticket
					...WatchersList_ticket
					...AiSummaryBlock_ticket
					...AiSuggestedActionsList_ticket
					linkedTasks {
						id
						timeRegistrations {
							edges {
								node {
									id
								}
							}
						}
					}
				}
				timeline {
					...ActivityTimelineItem_activity
				}
				attachments {
					id
					filename
					contentType
					sizeBytes
					downloadUrl
					uploadedBy {
						fullName
						...ticketingPerson_person
					}
					uploadedByName
					createdAt
				}
			}
		}
	}
`;

/**
 * Minimal existence-check lookups used by `GlobalSearch` to resolve a
 * `#<value>` query to a detail page (slug or raw id) via the Forecast seam's
 * `forecastFetchQuery`. Kept deliberately small — these never render through
 * `ForecastQueryRenderer`, so no `viewer { actualPersonId }` selection is needed.
 */
export const TicketLookupBySlugQuery = graphql`
	query useTicketDetailLookupBySlugQuery($slug: String!) {
		viewer {
			ticketDetailBySlug(slug: $slug) {
				ticket {
					id
					slug
				}
			}
		}
	}
`;

export const TicketLookupByIdQuery = graphql`
	query useTicketDetailLookupByIdQuery($id: ID!) {
		viewer {
			ticketDetail(id: $id) {
				ticket {
					id
					slug
				}
			}
		}
	}
`;

export function useTicketDetailFetchKey() {
	const [fetchKey, setFetchKey] = React.useState(0);
	const refetch = React.useCallback(() => setFetchKey(k => k + 1), []);
	return {fetchKey, refetch};
}
